/**
 * BlogListingPage.jsx
 * 
 * Public page showing all published blog posts.
 * Sleek, modern design with hero visual and white background.
 * Includes SEO meta tags for search engine visibility.
 */

import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import { ChevronLeft, BookOpen, Loader2, Calendar, ArrowRight } from 'lucide-react';
import { BACKEND_URL } from '../config/api';
import { SmoothHero } from '../components/SmoothHero';

const fetcher = url => fetch(url).then(r => r.json());

// Hero background images (rotating)
const HERO_IMAGES = [
  'https://customer-assets.emergentagent.com/job_booking-enhancer/artifacts/de6scz29_julian-gentilezza-IWoHLg2-UQU-unsplash.jpg',
  'https://customer-assets.emergentagent.com/job_booking-enhancer/artifacts/zpfybm4w_marcelo-cidrack-7jZNgIuJrCM-unsplash.jpg',
  'https://customer-assets.emergentagent.com/job_booking-enhancer/artifacts/ogkmrw0v_valna-studio-tPKQwYHy8q4-unsplash.jpg',
  'https://customer-assets.emergentagent.com/job_booking-enhancer/artifacts/m1i48894_simon-maage-tXiMrX3Gc-g-unsplash.jpg',
  'https://customer-assets.emergentagent.com/job_booking-enhancer/artifacts/e8lqe82n_hannah-morgan-dnkM5wPjVdg-unsplash.jpg',
  'https://customer-assets.emergentagent.com/job_booking-enhancer/artifacts/p74rwzah_jean-philippe-delberghe-tSXhGzkE7oU-unsplash.jpg',
  'https://customer-assets.emergentagent.com/job_booking-enhancer/artifacts/piufodyd_alexander-grey-YTGt96tGxSY-unsplash.jpg',
  'https://customer-assets.emergentagent.com/job_booking-enhancer/artifacts/vlcqzkzx_felix-rostig-UmV2wr-Vbq8-unsplash.jpg',
  // New user-provided images
  'https://customer-assets.emergentagent.com/job_seo-content-studio/artifacts/picjn2d8_israel-palacio--01v72yYhWU-unsplash.jpg',
  'https://customer-assets.emergentagent.com/job_seo-content-studio/artifacts/r8q0fwxc_nik-5dgHxsvJ170-unsplash.jpg',
  'https://customer-assets.emergentagent.com/job_seo-content-studio/artifacts/ioprpgq7_ball-park-brand-uHKoydAIjHw-unsplash.jpg',
  'https://customer-assets.emergentagent.com/job_seo-content-studio/artifacts/vibumac7_vika-strawberrika-o0EBbE1651A-unsplash.jpg',
  'https://customer-assets.emergentagent.com/job_seo-content-studio/artifacts/2torrkyh_josiah-gardner-Tb9FJ4Znz-U-unsplash.jpg',
];

const BlogListingPage = () => {
  const navigate = useNavigate();
  
  // Manually set meta tags as a fallback
  useEffect(() => {
    document.title = 'Blog | BookingFlows - Stories from Amazing Operators';
    
    // Set description
    let descMeta = document.querySelector('meta[name="description"]');
    if (!descMeta) {
      descMeta = document.createElement('meta');
      descMeta.name = 'description';
      document.head.appendChild(descMeta);
    }
    descMeta.content = 'Discover stories and insights from our amazing operators. Read about unique experiences, travel tips, and adventures from businesses on BookingFlows.';
    
    // Set keywords
    let keywordsMeta = document.querySelector('meta[name="keywords"]');
    if (!keywordsMeta) {
      keywordsMeta = document.createElement('meta');
      keywordsMeta.name = 'keywords';
      document.head.appendChild(keywordsMeta);
    }
    keywordsMeta.content = 'travel blog, experience stories, tour operator blog, adventure stories, travel tips, booking experiences';
    
    // Set OG tags
    const ogTags = [
      { property: 'og:type', content: 'website' },
      { property: 'og:title', content: 'Blog | BookingFlows' },
      { property: 'og:description', content: 'Discover stories and insights from our amazing operators.' },
      { property: 'og:url', content: window.location.href },
    ];
    
    ogTags.forEach(({ property, content }) => {
      let meta = document.querySelector(`meta[property="${property}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }
      meta.content = content;
    });
    
    // Set canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = `${window.location.origin}/blog`;
  }, []);
  
  const { data, isLoading, error } = useSWR(
    `${BACKEND_URL}/api/v2/blogs/public/blogs?limit=20`,
    fetcher,
    { revalidateOnFocus: false }
  );
  
  const blogs = data?.blogs || [];

  // BookingFlows-authored posts (features, integrations, comparisons)
  const { data: platformData } = useSWR(
    `${BACKEND_URL}/api/v2/public/platform-blog?limit=50`,
    fetcher,
    { revalidateOnFocus: false }
  );
  const platformPosts = platformData?.posts || [];
  
  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100 pt-[env(safe-area-inset-top)]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              <button 
                onClick={() => navigate('/')}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-gray-500" />
              </button>
              <Link to="/" className="flex items-center gap-2">
                <img 
                  src="/logo-icon.png" 
                  alt="BookingFlows" 
                  className="h-7 w-7 object-contain rounded-lg"
                />
                <span className="text-base font-semibold text-gray-900 hidden sm:inline">BookingFlows</span>
              </Link>
            </div>
          </div>
        </div>
      </nav>
      
      {/* Hero — instant gradient, fade-in, slow crossfade rotation */}
      <SmoothHero images={HERO_IMAGES} alt="Blog" className="h-64 sm:h-80" interval={9000}>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/60" />
        <div className="relative h-full flex flex-col items-center justify-center text-center px-4">
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3">Blog</h1>
          <p className="text-white/80 text-sm sm:text-base max-w-md">
            Stories and insights from our operators
          </p>
        </div>
      </SmoothHero>
      
      {/* Content */}
      <main className="flex-1 max-w-3xl mx-auto px-4 py-12 w-full">
        {/* From BookingFlows — platform features, integrations & comparisons */}
        {platformPosts.length > 0 && (
          <section className="mb-12" data-testid="platform-blog-section">
            <div className="flex items-center gap-2 mb-4">
              <img src="/logo-icon.png" alt="" className="h-5 w-5 rounded" />
              <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wide">From BookingFlows</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {platformPosts.map((p) => (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  className="block p-4 rounded-xl border border-gray-100 hover:border-blue-200 hover:bg-blue-50/30 transition-colors group"
                  data-testid={`platform-post-${p.slug}`}
                >
                  <span className="inline-block px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[10px] font-bold uppercase tracking-wide mb-2">
                    {p.category}
                  </span>
                  <h3 className="text-sm font-semibold text-gray-900 group-hover:text-blue-700 line-clamp-2 leading-snug">{p.title}</h3>
                  <p className="text-xs text-gray-500 mt-1 line-clamp-2">{p.excerpt}</p>
                </Link>
              ))}
            </div>
            <div className="flex items-center gap-2 mt-8 mb-0">
              <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Stories from our operators</h2>
            </div>
          </section>
        )}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-sm">Unable to load blog posts. Please try again later.</p>
          </div>
        ) : blogs.length === 0 ? (
          <div className="text-center py-16">
            <div 
              className="w-14 h-14 bg-gray-50 rounded-2xl flex items-center justify-center mx-auto mb-4"
              style={{ boxShadow: '0 4px 24px -4px rgba(0, 0, 0, 0.08)' }}
            >
              <BookOpen className="w-6 h-6 text-gray-400" />
            </div>
            <h2 className="text-base font-semibold text-gray-900 mb-1">No blog posts yet</h2>
            <p className="text-gray-500 text-sm mb-6">Check back soon for stories from our operators!</p>
            <Link to="/">
              <button className="px-5 py-2 bg-slate-900 text-white text-sm rounded-full hover:bg-slate-800 transition-colors">
                Explore Experiences
              </button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {blogs.map((blog) => (              <Link 
                key={blog.id}
                to={`/blog/${blog.organization?.slug}`}
                className="block bg-white rounded-xl overflow-hidden group transition-all hover:translate-y-[-2px]"
                style={{ boxShadow: '0 4px 24px -4px rgba(0, 0, 0, 0.08), 0 2px 8px -2px rgba(0, 0, 0, 0.04)' }}
              >
                <div className="flex items-stretch">
                  {/* Left - Logo/Icon */}
                  <div className="w-24 sm:w-32 bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center flex-shrink-0">
                    {blog.organization?.logo_url ? (
                      <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white rounded-lg p-1.5 flex items-center justify-center">
                        <img 
                          src={blog.organization.logo_url}
                          alt={blog.organization.name}
                          className="w-full h-full object-contain"
                        />
                      </div>
                    ) : (
                      <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white rounded-lg flex items-center justify-center">
                        <BookOpen className="w-6 h-6 text-slate-700" />
                      </div>
                    )}
                  </div>
                  
                  {/* Right - Content */}
                  <div className="flex-1 p-4 sm:p-5 flex flex-col justify-center min-w-0">
                    <h2 className="font-semibold text-gray-900 text-sm sm:text-base mb-1 line-clamp-1 group-hover:text-blue-600 transition-colors">
                      {blog.title}
                    </h2>
                    <p className="text-xs sm:text-sm text-gray-500 mb-2 line-clamp-1">
                      {blog.excerpt}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 text-xs text-gray-400">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(blog.published_at).toLocaleDateString('en-US', { 
                            month: 'short', day: 'numeric'
                          })}
                        </span>
                        <span className="hidden sm:inline">{blog.organization?.name}</span>
                      </div>
                      <span className="text-blue-600 text-xs font-medium flex items-center gap-1 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity">
                        Read <ArrowRight className="w-3 h-3" />
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
      
      {/* Footer */}
      <footer className="py-8 px-4 bg-white border-t border-gray-100 mt-auto">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img 
                src="/logo-icon.png" 
                alt="BookingFlows" 
                className="h-6 w-6 object-contain rounded-lg"
              />
              <span className="text-sm font-semibold text-gray-900">BookingFlows</span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-gray-500">
              <Link to="/" className="hover:text-gray-900 transition-colors">Home</Link>
              <Link to="/search" className="hover:text-gray-900 transition-colors">Experiences</Link>
              <Link to="/for-suppliers" className="hover:text-gray-900 transition-colors">For Operators</Link>
            </div>
            <p className="text-xs text-gray-400">
              © {new Date().getFullYear()} BookingFlows
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BlogListingPage;
